import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { AppTooltip } from '../Base'
import './VsPreviousPriceToggle.css'


// Estructura por defecto de la botonera. Se usa mientras el backend aún no ha respondido
// (discovery en curso) — todos los chips disabled — para que el control no aparezca/
// desaparezca y mantenga su layout estable. Debe quedar alineado con
// PREVIOUS_DIFF_TARGETS del backend (Competition/Packages previous svc).
const DEFAULT_DIFF_TARGETS = [
    { target: 3,  tolerance: 1, actual: null, date: null, enabled: false },
    { target: 5,  tolerance: 1, actual: null, date: null, enabled: false },
    { target: 7,  tolerance: 2, actual: null, date: null, enabled: false },
    { target: 14, tolerance: 3, actual: null, date: null, enabled: false },
    { target: 28, tolerance: 5, actual: null, date: null, enabled: false },
]

// Formatea date_int (YYYYMMDD) a YYYY-MM-DD para el popup.
function formatDateInt(dateInt) {
    if (!dateInt) return ''
    const s = String(dateInt)
    if (s.length !== 8) return s
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
}

function chipTitle(entry, t) {
    if (!entry?.enabled) return t('vsPreviousChip.noData', { target: entry?.target ?? '?', tolerance: entry?.tolerance ?? '?' })
    return t('vsPreviousChip.daysAgo', { count: entry.actual, date: formatDateInt(entry.date) })
}

/**
 * Botonera "vs previous price". UI 100% dumb: recibe los chips ya calculados por backend
 * (POST /api/<vertical>/previous-data) y emite un único callback `onSelect(target | null)`.
 *
 * Props:
 *   availableDiffDays: [{ target, tolerance, actual, date, enabled }, ...]
 *   selected: { target, actual, date } | number | null  — el provider del dashboard suele
 *             pasarlo como objeto para conservar la fecha real del chip activo (que se usa
 *             en tooltips downstream). Por compat se acepta también el target integer.
 *   onSelect: (target: number | null) => void
 *   isLoading?: boolean
 *
 * Comportamiento (decisión G del plan):
 *   - Clic en chip inactivo → onSelect(target)
 *   - Clic en chip activo  → onSelect(null)   (apaga el toggle)
 *   - Cambio entre chips    → onSelect(otroTarget)
 *
 * El componente NO conoce el contexto del dashboard. Cada vertical envuelve este
 * componente con su propio hook `useVsPreviousPrice` (Fase 4/5).
 */
export default function VsPreviousPriceToggle({ availableDiffDays = [], selected = null, onSelect, isLoading = false }) {

    const { t } = useTranslation('common')
    // Normaliza `selected` a target integer para las comparaciones internas.
    const selectedTarget = typeof selected === 'object' && selected !== null ? selected.target : selected

    const handleClick = (target, enabled) => () => {
        if (!enabled || isLoading || typeof onSelect !== 'function') return
        onSelect(selectedTarget === target ? null : target)
    }

    // Mientras el backend no ha respondido (discovery en curso o filtros sin resultado),
    // pintamos la botonera completa por defecto con todos los chips disabled — así el
    // control no aparece/desaparece de la cabecera.
    const chips = availableDiffDays?.length ? availableDiffDays : DEFAULT_DIFF_TARGETS

    return <div className="vs-previous-toggle" data-tour="vs-previous-toggle">
        <span
            className="vs-previous-toggle__spinner"
            style={{ visibility: isLoading ? 'visible' : 'hidden' }}
            aria-label={isLoading ? t('state.loading') : undefined}
            aria-hidden={!isLoading}
        />
        <span className="vs-previous-toggle__label">{t('vsPrevious')}</span>
        <div className={`vs-previous-toggle__days ${isLoading ? 'vs-previous-toggle__days--loading' : ''}`}>
            {chips.map(entry => {
                const active = entry.enabled && selectedTarget === entry.target
                const classes = [
                    'vs-previous-toggle__day',
                    active && 'vs-previous-toggle__day--active',
                    !entry.enabled && 'vs-previous-toggle__day--disabled',
                ].filter(Boolean).join(' ')
                return <AppTooltip key={entry.target} title={chipTitle(entry, t)}>
                    <button
                        type="button"
                        className={classes}
                        disabled={!entry.enabled || isLoading}
                        onClick={handleClick(entry.target, entry.enabled)}
                    >
                        {entry.target}d
                    </button>
                </AppTooltip>
            })}
        </div>
    </div>
}
